import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, useToast } from './components/Toast';
import { initBackButton, setPageController } from './backButton';
import { SyncProvider } from './context/SyncContext';
import UpdateBanner from './components/UpdateBanner';
import UpdateGate from './components/UpdateGate';
import InstallAppPrompt from './components/InstallAppPrompt';
import { NotificationProvider } from './context/NotificationContext';
import { ModalProvider } from './components/ModalProvider';
import { ChatProvider } from './context/ChatContext';
import AttendanceCheckoutPopup from './components/AttendanceCheckoutPopup';
import ChatWidget from './components/ChatWidget';
import Sidebar from './components/Sidebar';
import SeasonalBackground from './components/SeasonalBackground';
import SyncIndicator from './components/SyncIndicator';
import AnnouncementBar from './components/AnnouncementBar';
import AutoSync from './components/AutoSync';
import SyncHelpPopup from './components/SyncHelpPopup';
import WeeklyReportReminder from './components/WeeklyReportReminder';
import NotificationBell from './components/NotificationBell';
import FirstSetupModal from './components/FirstSetupModal';
import Login from './pages/Login';
import DashboardPage from './pages/DashboardPage';
import DrivePage from './pages/DrivePage';
import ScanPage from './pages/ScanPage';
import YahooScanPage from './pages/YahooScanPage';
import TasksPage from './pages/TasksPage';
import CustomersPage from './pages/CustomersPage';
import StaffPage from './pages/StaffPage';
import KpiPage from './pages/KpiPage';
import OvertimePage from './pages/OvertimePage';
import SyncPage from './pages/SyncPage';
import ErrorBoundary from './components/ErrorBoundary';
import ComingSoonPage from './pages/ComingSoonPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import MeetingsPage from './pages/MeetingsPage';
import PurchaseReportTabs from './pages/PurchaseReportTabs';
import CskhPage from './pages/CskhPage';
import NotesPage from './pages/NotesPage';
import FinanceReportPage from './pages/FinanceReportPage';
import CskhReportPage from './pages/CskhReportPage';
import BusinessReportPage from './pages/BusinessReportPage';
import WeeklyReportPage from './pages/WeeklyReportPage';
import CaNguaPage from './pages/CaNguaPage';
import ExchangeLogPage from './pages/ExchangeLogPage';
import LionjobHub from './pages/LionjobHub';
import PayrollAdminPage from './pages/PayrollAdminPage';
import RefundProposalPage from './pages/RefundProposalPage';
import GuidePage from './pages/GuidePage';
import ChatPage from './pages/ChatPage';
import ChatTasksHubPage from './pages/ChatTasksHubPage';
import ChatDailyReportPage from './pages/ChatDailyReportPage';
import MobileNav from './components/MobileNav';
import MobileMenuPage from './pages/MobileMenuPage';
import MobileOpsPage from './pages/MobileOpsPage';
import MobileAttendance from './pages/MobileAttendance';
import OpsLookupPage from './pages/ops/OpsLookupPage';
import OpsPickPage from './pages/ops/OpsPickPage';
import OpsCollectPage from './pages/ops/OpsCollectPage';
import OpsDeliveryPage from './pages/ops/OpsDeliveryPage';
import OpsImagesPage from './pages/ops/OpsImagesPage';
import OpsNotifyPage from './pages/ops/OpsNotifyPage';
import { OPS_ITEMS, OPS_KEYS, canSeeOps, isOpsStaff } from './menuConfig';

// Các trang theo vai trò (để chặn khôi phục trang không có quyền sau khi F5).
const PAGES_BY_ROLE = {
  GD: ['menu', 'attendance', 'guide', 'dashboard', 'messages', 'chat-tasks', 'chat-report', 'scan', 'yahoo-scan', 'tasks', 'customers', 'cskh', 'staff', 'exchange-log', 'kpi', 'overtime', 'overtime-op', 'overtime-ketoan', 'weekly-report', 'purchase-report', 'business-report', 'finance-report', 'refund', 'cskh-report', 'announcements', 'meetings', 'notes', 'drive', 'cangua', 'sync', 'lionjob', 'payroll', 'ops', ...OPS_KEYS],
  TP: ['menu', 'attendance', 'guide', 'dashboard', 'messages', 'chat-tasks', 'chat-report', 'scan', 'yahoo-scan', 'tasks', 'customers', 'cskh', 'kpi', 'overtime', 'overtime-op', 'overtime-ketoan', 'weekly-report', 'purchase-report', 'business-report', 'finance-report', 'refund', 'cskh-report', 'announcements', 'meetings', 'notes', 'drive', 'cangua', 'sync', 'payroll', 'ops', ...OPS_KEYS],
  NV: ['menu', 'attendance', 'guide', 'dashboard', 'messages', 'chat-tasks', 'chat-report', 'yahoo-scan', 'tasks', 'customers', 'cskh', 'kpi', 'overtime', 'overtime-op', 'overtime-ketoan', 'purchase-report', 'business-report', 'finance-report', 'refund', 'cskh-report', 'announcements', 'meetings', 'notes', 'drive', 'cangua', 'payroll', 'ops', ...OPS_KEYS],
  // ĐỐI TÁC (role=DT): CHỈ Tin nhắn. Mọi trang khác bị chặn (backend cũng 403 — xem middleware protect).
  DT: ['messages', 'drive'],
};

function Shell() {
  const { me, loading } = useAuth();
  const [page, setPageRaw] = useState(() => localStorage.getItem('ft_page') || 'dashboard');
  // Trang "cha" để quay lại: mở 1 chức năng từ màn Vận hành thì nhớ 'ops' -> màn con hiện nút back
  // (và nút back cứng Android cũng về đó thay vì nhảy thẳng Trang chủ). Đổi trang kiểu khác -> xoá.
  const [backTo, setBackTo] = useState('');
  const setPage = useCallback((key, from = '') => {
    setBackTo(typeof from === 'string' ? from : '');
    setPageRaw(key);
  }, []);
  const [refreshKey, setRefreshKey] = useState(0);

  // Lưu trang hiện tại để F5 giữ nguyên vị trí.
  useEffect(() => {
    if (page) localStorage.setItem('ft_page', page);
  }, [page]);

  // Nút BACK cứng Android (app native): đóng modal/hội thoại -> về trang chủ -> bấm 2 lần thoát.
  const toast = useToast();
  const pageRef = useRef(page);
  const backToRef = useRef(backTo);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { backToRef.current = backTo; }, [backTo]);
  useEffect(() => {
    setPageController({ getPage: () => pageRef.current, getParent: () => backToRef.current, setPage, homePage: 'dashboard', toast });
    initBackButton();
  }, [setPage, toast]);

  // Bấm THÔNG BÁO (đẩy) -> nhảy sang trang Tin nhắn để mở hội thoại CHI TIẾT (không dừng ở màn danh
  // sách). ChatContext set deepConv + bắn 'ft:open-conv-page'; ChatPage tự setSel. App là CHA của
  // ChatProvider nên không useChat được -> nhận qua window event.
  useEffect(() => {
    const go = () => setPage('messages');
    window.addEventListener('ft:open-conv-page', go);
    return () => window.removeEventListener('ft:open-conv-page', go);
  }, []);

  // Heartbeat có mặt theo IP: khi NV/TP đang mở app, ping mỗi ~3 phút để biết còn ở mạng công ty.
  useEffect(() => {
    if (!me || (me.role !== 'NV' && me.role !== 'TP')) return;
    let alive = true;
    // Bỏ qua giờ nghỉ trưa 11:45–13:15 (giờ VN).
    const isLunch = () => {
      const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date());
      return p >= '11:45' && p <= '13:15';
    };
    const ping = () => {
      // KHÔNG đòi tab phải đang hiện: NV ngồi ở công ty mở sẵn tab FunnyTask nhưng làm việc ở
      // tab/ứng dụng khác thì tab ở trạng thái hidden -> trước đây ngưng ping -> server tưởng
      // vắng -> cảnh báo oan "không có mặt ở công ty". Mở app là đủ để biết đang ở mạng cty.
      if (!(alive && !isLunch())) return;
      Promise.all([import('./api/attendance'), import('./utils/publicIp')])
        .then(([m, ipm]) => ipm.getPublicIp().then((ip) => m.presencePing(ip).catch(() => {})));
    };
    ping();
    const t = setInterval(ping, 5 * 60 * 1000); // 5' — PHẢI khớp PING_MINUTES ở onlineAttendanceController
    document.addEventListener('visibilitychange', ping);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', ping); };
  }, [me]);

  // Sau khi biết vai trò, nếu trang đã lưu không thuộc quyền thì về trang chủ.
  useEffect(() => {
    if (!me) return;
    const isAdmin = me.role === 'GD' || me.role === 'CV';
    // Trưởng phòng ban (DepartmentHead, kể cả role NV) NGANG QUYỀN trưởng nhóm KD (role TP):
    // dùng chung danh sách trang của TP.
    const isDeptHead = (me.deptHeadOf || []).length > 0;
    // Phó phòng KD (role NV + isDeputy): dùng chung danh sách trang của TP (menu + backend là chốt chặn thật).
    const isTeamLead = me.role === 'TP' || isDeptHead || me.isDeputy;
    const allowed = isAdmin ? PAGES_BY_ROLE.GD : (isTeamLead ? PAGES_BY_ROLE.TP : PAGES_BY_ROLE[me.role]) || [];
    // Quản lý nhân viên: GĐ/CV + TRƯỞNG PHÒNG KẾ TOÁN (full quyền như GĐ).
    const canStaffAdmin = isAdmin || (me.deptHeadOf || []).includes('Phòng kế toán');
    // Phòng kế toán không xem Danh sách quét / CSKH / Khách hàng. Phòng DVKH không xem CSKH —
    // TRỪ khi kiêm Phòng CSKH ("thuộc phòng nào có quyền phòng đó").
    const depts = me.departments || [];
    const inCSKH = depts.includes('Phòng CSKH');
    const isAccounting = !isAdmin && depts.includes('Phòng kế toán');
    const isDVKH = !isAdmin && depts.includes('Phòng DVKH');
    // Trưởng phòng OP: KHÔNG đọc các báo cáo (chỉ được Biên bản họp).
    const isOpHead = (me.deptHeadOf || []).includes('Phòng OP');
    const opBlockedReport = isOpHead && ['weekly-report', 'purchase-report', 'business-report', 'cskh-report', 'finance-report'].includes(page);
    const blocked =
      (isAccounting && ['scan', 'cskh', 'customers'].includes(page)) || (isDVKH && !inCSKH && page === 'cskh') || opBlockedReport;
    // Danh sách quét + Đồng bộ: GĐ/CV + trưởng nhóm KD + trưởng phòng ban.
    const canScan = isAdmin || (me.role === 'TP' && !!me.group) || isDeptHead || me.isDeputy;
    const blockedScan = page === 'scan' && !canScan;
    const blockedSync = page === 'sync' && !canScan;
    // Lionjob: CHỈ role GD (một mình Giám đốc) — Cố vấn CV không được vào.
    const blockedLionjob = page === 'lionjob' && me.role !== 'GD';
    const opsItem = OPS_ITEMS.find((i) => i.key === page);
    const blockedOps = !!opsItem && !canSeeOps(me, opsItem);
    // Màn gom chức năng Vận hành: chỉ nhân sự Phòng OP (người duy nhất có tab này ở thanh dưới).
    const blockedOpsHub = page === 'ops' && !isOpsStaff(me);
    // Màn Bảng lương: mọi role vào được (chỉ xem của mình); tab "Tổng hợp" gate bên trong.
    // ĐỐI TÁC (DT) không có trang chủ -> đưa thẳng về Tin nhắn.
    const home = me.role === 'DT' ? 'messages' : 'dashboard';
    const pageOk = allowed.includes(page) || (canStaffAdmin && page === 'staff');
    if (!pageOk || blocked || blockedScan || blockedSync || blockedLionjob || blockedOps || blockedOpsHub) setPage(home);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (loading) return null;
  if (!me) return (<><Login /><InstallAppPrompt /></>);

  // ĐỐI TÁC (role=DT): giao diện TỐI GIẢN — chỉ khung Tin nhắn, KHÔNG kèm mọi chrome nội bộ
  // (thanh thông báo, chuông, đồng bộ, nhắc báo cáo, popup chấm công…) để không lộ gì + không gọi
  // API bị chặn. Bảo mật thật nằm ở backend; đây chỉ là giao diện gọn cho đối tác.
  if (me.role === 'DT') {
    const dt = page === 'drive' ? 'drive' : 'messages';
    const dtBtn = (key, icon, label) => (
      <button
        onClick={() => setPage(key)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, fontWeight: 500,
          border: 'none', borderRadius: 8, cursor: 'pointer',
          background: dt === key ? 'var(--accent)' : 'transparent',
          color: dt === key ? '#fff' : 'var(--text2, #555)',
        }}
      >
        <i className={'ti ' + icon} /> {label}
      </button>
    );
    return (
      <div id="app" className="app-partner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid var(--border, #e5e7eb)', background: 'var(--panel, #fff)', flex: 'none' }}>
          {dtBtn('messages', 'ti-message-circle', 'Tin nhắn')}
          {dtBtn('drive', 'ti-folders', 'Kho file')}
        </div>
        <main className="main">
          <ErrorBoundary resetKey={dt}>
            {dt === 'drive' ? <DrivePage /> : <ChatPage />}
          </ErrorBoundary>
        </main>
        <InstallAppPrompt />
      </div>
    );
  }

  // Vào chức năng vận hành TỪ màn Vận hành -> màn con hiện nút back quay lại danh sách đó.
  const opsBack = backTo === 'ops' ? () => setPage('ops') : undefined;

  return (
    <div id="app">
      <SeasonalBackground />
      <Sidebar page={page} setPage={setPage} />
      <main className="main">
        <AnnouncementBar onOpen={() => setPage('announcements')} />
        <ErrorBoundary resetKey={page}>
        {page === 'dashboard' && <DashboardPage />}
        {page === 'messages' && <ChatPage />}
        {page === 'drive' && <DrivePage />}
        {page === 'chat-tasks' && <ChatTasksHubPage />}
        {page === 'chat-report' && <ChatDailyReportPage />}
        {page === 'scan' && <ScanPage key={refreshKey} onCreated={() => setRefreshKey((k) => k + 1)} />}
        {page === 'yahoo-scan' && <YahooScanPage />}
        {page === 'tasks' && <TasksPage />}
        {page === 'customers' && <CustomersPage />}
        {page === 'cskh' && <CskhPage />}
        {page === 'staff' && <StaffPage />}
        {page === 'exchange-log' && <ExchangeLogPage />}
        {page === 'kpi' && <KpiPage />}
        {page === 'overtime' && <OvertimePage dept="Phòng DVKH" />}
        {page === 'overtime-op' && <OvertimePage dept="Phòng OP" hoursOnly />}
        {page === 'overtime-ketoan' && <OvertimePage dept="Phòng kế toán" />}
        {page === 'purchase-report' && <PurchaseReportTabs />}
        {page === 'business-report' && <BusinessReportPage />}
        {page === 'weekly-report' && <WeeklyReportPage />}
        {page === 'finance-report' && <FinanceReportPage />}
        {page === 'cskh-report' && <CskhReportPage />}
        {page === 'announcements' && <AnnouncementsPage />}
        {page === 'meetings' && <MeetingsPage />}
        {page === 'notes' && <NotesPage />}
        {page === 'cangua' && <CaNguaPage />}
        {page === 'sync' && <SyncPage />}
        {page === 'lionjob' && <LionjobHub />}
        {page === 'payroll' && <PayrollAdminPage />}
        {page === 'refund' && <RefundProposalPage />}
        {page === 'guide' && <GuidePage />}
        {page === 'menu' && <MobileMenuPage setPage={setPage} />}
        {page === 'attendance' && <MobileAttendance />}
        {page === 'ops' && <MobileOpsPage setPage={setPage} />}
        {page === 'ops-lookup' && <OpsLookupPage onBack={opsBack} />}
        {page === 'ops-pick' && <OpsPickPage onBack={opsBack} />}
        {page === 'ops-collect' && <OpsCollectPage onBack={opsBack} />}
        {page === 'ops-waiting' && <OpsDeliveryPage mode="WAITING" onBack={opsBack} />}
        {page === 'ops-delivering' && <OpsDeliveryPage mode="PENDING" onBack={opsBack} />}
        {page === 'ops-delivered' && <OpsDeliveryPage mode="SUCCESS" onBack={opsBack} />}
        {page === 'ops-images' && <OpsImagesPage onBack={opsBack} />}
        {page === 'ops-notify' && <OpsNotifyPage onBack={opsBack} />}
        </ErrorBoundary>
      </main>
      <SyncIndicator />
      <NotificationBell />
      <AutoSync />
      <SyncHelpPopup />
      <WeeklyReportReminder onOpenReport={() => setPage('business-report')} />
      <ChatWidget />
      {/* Thanh điều hướng dưới — chỉ hiện trên mobile (CSS) */}
      <MobileNav page={page} setPage={setPage} />
      {me.mustChangePassword && <FirstSetupModal />}
      <InstallAppPrompt />

    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ToastProvider>
          <SyncProvider>
            <ModalProvider>
              <ChatProvider>
                <Shell />
                {/* Popup nhắc chấm công ra cuối giờ (đè lên mọi màn). */}
                <AttendanceCheckoutPopup />
                {/* Nhắc tải lại khi server vừa deploy bản mới (đặt ngoài Shell -> màn đăng nhập cũng có). */}
                <UpdateBanner />
                {/* App native: nhắc/bắt cập nhật khi có APK/bản mới (web bỏ qua). */}
                <UpdateGate />
              </ChatProvider>
            </ModalProvider>
          </SyncProvider>
        </ToastProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
